import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Logger } from 'common/utility/logger/logger';
import { SelectableItem } from 'components/item-selector/item-selector.component';
import { catchError, debounceTime, EMPTY, of, Subject, take, takeUntil } from 'rxjs';
import { EdgeDevice, EdgeDeviceService } from 'services/edge-device/edge-device.service';
import { NotificationService } from 'services/notification/notification.service';
import { OrganizationService } from 'services/organization/organization.service';

export interface Organization {
  id: string;
  identifier: string;
}

export interface OrgDeviceSelection {
  organization: Organization;
  deviceIds: string[];
}

@Component({
  selector: 'ems-org-device-assignment',
  templateUrl: './org-device-assignment.component.html',
  styleUrls: ['./org-device-assignment.component.scss']
})
export class OrgDeviceAssignmentComponent implements OnInit, OnDestroy {

  @Input() selectedDeviceIds: string[] = [];
  @Output() selectionChange = new EventEmitter<OrgDeviceSelection>();

  organizations: Organization[] = [];
  selectedOrganization: Organization | null = null;
  selectorItems: SelectableItem[] = [];

  private unsubscribe = new Subject<void>();
  private deviceSelectionChanged = new Subject<string[]>();
  private pendingDeviceIds: string[] | null = null;

  constructor(
    private edgeDeviceService: EdgeDeviceService,
    private organizationService: OrganizationService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.organizationService.list().pipe(
      take(1),
      takeUntil(this.unsubscribe),
      catchError(() => of([]))
    ).subscribe((orgs: any[]) => {
      this.organizations = orgs.map(o => ({ id: o.id ?? o.identifier, identifier: o.identifier ?? o.id }));
      if (this.organizations.length) {
        this.selectOrganization(this.organizations[0]);
      }
    });

    this.edgeDeviceService.list().pipe(
      take(1),
      takeUntil(this.unsubscribe)
    ).subscribe((devices: EdgeDevice[]) => {
      this.selectorItems = devices.map(d => ({ id: d.edgeId, name: d.label || d.edgeId }));
    });

    this.deviceSelectionChanged.pipe(
      debounceTime(3000),
      takeUntil(this.unsubscribe)
    ).subscribe((deviceIds: string[]) => {
      this.saveDeviceAssignments(deviceIds);
    });
  }

    ngOnDestroy(): void {
    if (this.pendingDeviceIds) {
      this.saveDeviceAssignments(this.pendingDeviceIds);
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private saveDeviceAssignments(deviceIds: string[]): void {
    if (!this.selectedOrganization) return;
    this.organizationService.setEdgeDevices(this.selectedOrganization.identifier, deviceIds).pipe(
      take(1),
      catchError(err => {
        Logger.error(`Failed to save device assignments: ${err}`)
        this.notificationService.notify({ message: `Failed to save device assignments`, notificationType: 'error' })
        return EMPTY;
      })
    ).subscribe(() => {
      this.pendingDeviceIds = null;
      this.notificationService.notify({ message: `Device Assignments Saved` })
    });
  }

  selectOrganization(org: Organization): void {
    this.selectedOrganization = org;
    this.selectedDeviceIds = [];
    this.emit();

    this.organizationService.getEdgeDevices(org.identifier).pipe(
      take(1),
      takeUntil(this.unsubscribe),
      catchError(() => of([]))
    ).subscribe((devices: EdgeDevice[]) => {
      this.selectedDeviceIds = devices.map(device => device.edgeId);
      this.emit();
    });
  }

  onDeviceSelectionChange(deviceIds: string[]): void {
    this.selectedDeviceIds = deviceIds;
    this.emit();
    this.pendingDeviceIds = deviceIds;
    this.deviceSelectionChanged.next(deviceIds);
  }

  private emit(): void {
    if (!this.selectedOrganization) return;
    this.selectionChange.emit({
      organization: this.selectedOrganization,
      deviceIds: this.selectedDeviceIds,
    });
  }
}
